import { computed, ref, watch, type Ref } from 'vue';
import { optionLetter, type AnswersByQuestion, type MatrixColumn, type MatrixPlayer } from '../util/answerRecapMatrix';

export type BreakdownAlternative = {
  id: string;
  letter: string;
  text: string;
  count: number;
  pct: number;
  isCorrect: boolean;
  players: MatrixPlayer[];
};

type BreakdownSources = {
  isOpen: Ref<boolean>;
  column: Ref<MatrixColumn | null>;
  answersByQuestion: Ref<AnswersByQuestion>;
  players: Ref<MatrixPlayer[]>;
};

// Expansion resets on every open: collapsed lists are what keep player names off a
// classroom projector until the teacher asks for them.
export const useQuestionBreakdown = ({ isOpen, column, answersByQuestion, players }: BreakdownSources) => {
  const expanded = ref<Record<string, boolean>>({});

  watch(
    () => [isOpen.value, column.value?.questionId],
    () => {
      expanded.value = {};
    },
  );

  const alternatives = computed<BreakdownAlternative[]>(() => {
    const openColumn = column.value;
    if (!openColumn) {
      return [];
    }
    const answers = answersByQuestion.value[openColumn.questionId] ?? {};

    return openColumn.options.map((option, index) => {
      const chose = players.value.filter((player) => answers[player.id] === option.id);
      return {
        id: option.id,
        letter: optionLetter(index),
        text: option.value,
        count: chose.length,
        // Share of the whole class, matching the column's own % correct, so these sum to
        // under 100% when someone skipped.
        pct: players.value.length === 0 ? 0 : Math.round((chose.length / players.value.length) * 100),
        isCorrect: option.id === openColumn.correctAnswerId,
        players: chose,
      };
    });
  });

  return {
    alternatives,
    isExpanded: (optionId: string) => !!expanded.value[optionId],
    toggle: (optionId: string) => {
      expanded.value = { ...expanded.value, [optionId]: !expanded.value[optionId] };
    },
  };
};
